import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JobInfoSection from '../../components/institute/Jobs/JobPostForm/JobInfoSection';
import JobDescriptionSection from '../../components/institute/Jobs/JobPostForm/JobDescriptionSection';
import JobFormAction from '../../components/institute/Jobs/JobPostForm/JobFormAction';
import PageHeader from '../../components/PageHeader';

const InstituteAddPostPage = ({ 
  jobData = null, 
  isEdit = false, 
  onSave, 
  onCancel
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const currentJobId = params.jobId;
  const isEditMode = Boolean(currentJobId) || isEdit;

  const [formData, setFormData] = useState({
    employment_title: '',
    slug: '',
    employment_type: '',
    organisation_id: '',
    program_id: '',
    institute_id: '',
    created_by: '',
    updated_by: '',
    short_description: '',
    job_description: '',
    openings: '',
    duration: '',
    salary: '',
    expiry_date: '',
    skills: [],
    education: [],
    industry_id: '',
    degree_status: '',
    type_of_shift: '',
    city_id: [],
    status: '1'
  });

  const [organizationData, setOrganizationData] = useState([]);
  const [originalJobData, setOriginalJobData] = useState(null);
  const [jobSlugForUpdate, setJobSlugForUpdate] = useState('');

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: '', message: '' });

  const instituteId = sessionStorage.getItem("instituteId");
  const UserId = sessionStorage.getItem("id");
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  const API_BASE_URL = `${LMS}/api/employment`;
  const API_HEADERS = {
    'Authorization': token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${LMS}/api/organisation?institute_id=${parseInt(instituteId)}`, {
        method: 'GET',
        headers: API_HEADERS
      });
      const data = await response.json();
      setOrganizationData(data.organizations || data.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchJobData = async (jobId) => {
    try {
      setIsLoading(true);
      console.log('Fetching job data for ID:', jobId);
      
      const queryParams = new URLSearchParams({
        institute_type: "college",
        institute_id: parseInt(instituteId),
        emp_id: parseInt(jobId),
        paginate: 1,
        page: 1,
        per_page: 10
      });
      
      const response = await fetch(`${API_BASE_URL}?${queryParams}`, {
        method: 'GET',
        headers: API_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Method 1 - Fetched job data:', result);
      
      let jobData = null;
      if (result?.data?.employmentsByInstitute?.[0]) {
        jobData = result.data.employmentsByInstitute[0];
      } else if (result?.data && Array.isArray(result.data) && result.data.length > 0) {
        jobData = result.data[0];
      } else if (result?.data && !Array.isArray(result.data)) {
        jobData = result.data;
      } else if (result?.employment) {
        jobData = result.employment;
      } else if (result?.job) {
        jobData = result.job;
      } else if (result && typeof result === 'object' && result.id) {
        jobData = result;
      }

      console.log('Extracted job data:', jobData);

      if (jobData) {
        setOriginalJobData(jobData);
        setJobSlugForUpdate(jobData.slug || currentJobId.toString());
        populateFormData(jobData);
        
        setApiMessage({ 
          type: 'success', 
          message: 'Job data loaded successfully!' 
        });
        
        setTimeout(() => {
          setApiMessage({ type: '', message: '' });
        }, 3000);
      } else {
        throw new Error('Job data not found in response');
      }

    } catch (error) {
      console.error('Error fetching job data:', error);
      setApiMessage({ 
        type: 'error', 
        message: `Failed to load job data: ${error.message}. Please check if the job ID (${jobId}) is correct.` 
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadJobData = async () => {
      if (instituteId) {
        await fetchOrganizations();
      }

      if (isEditMode && currentJobId) {
        await fetchJobData(currentJobId);
      } else if (isEditMode && !currentJobId) {
        console.error('Edit mode detected but no job ID found in URL');
        setApiMessage({
          type: 'error',
          message: 'No job ID found in URL for editing. Please check the URL format.'
        });
      }
    };
    loadJobData();
  }, [instituteId, isEditMode, currentJobId]);

  const createEmployment = async (employmentData) => {
    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST', 
        headers: API_HEADERS,
        body: JSON.stringify(employmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error response data:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Success response:', result);
      return result;

    } catch (error) {
      console.error('Error creating employment:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  };

  const updateEmployment = async (employmentData, slug) => {
    try {
      const updateUrl = `${API_BASE_URL}/${slug}`;

      const response = await fetch(updateUrl, {
        method: 'PUT', 
        headers: API_HEADERS,
        body: JSON.stringify(employmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error response data:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    if (jobData && !isEditMode) {
      populateFormData(jobData);
    } else if (!isEditMode && !currentJobId) {
      setFormData(prev => ({
        ...prev,
        institute_id: parseInt(instituteId),
        created_by: parseInt(UserId),
        updated_by: parseInt(UserId),
        expiry_date: ''
      }));
    }
  }, [jobData, instituteId, UserId, isEditMode, currentJobId]);

  const populateFormData = (data) => {
    console.log('Populating form data with:', data);
    
    let skillsArray = [];
    if (Array.isArray(data.skills)) {
      skillsArray = data.skills.map(skill => {
        if (typeof skill === 'string') {
          return { id: skill, name: skill };
        }
        return { id: skill.id || skill.name || skill, name: skill.name || skill.skill_title || skill };
      });
    } else if (typeof data.skills === 'string' && data.skills) {
      skillsArray = data.skills.split(',').map(skill => ({ id: skill.trim(), name: skill.trim() }));
    }

    let educationArray = [];
    if (Array.isArray(data.education)) {
      educationArray = data.education.map(edu => {
        if (typeof edu === 'string') {
          return { id: edu, name: edu };
        }
        return { id: edu.id || edu.name || edu, name: edu.name || edu };
      });
    } else if (typeof data.education === 'string' && data.education) {
      educationArray = data.education.split(',').map(edu => ({ id: edu.trim(), name: edu.trim() }));
    }

    let shiftArray = [];
    if (Array.isArray(data.type_of_shift)) {
      shiftArray = data.type_of_shift;
    } else if (data.type_of_shift) {
      shiftArray = [data.type_of_shift];
    }

    let cityArray = [];
    if (data.job_locations && Array.isArray(data.job_locations)) {
      cityArray = data.job_locations.map(location => ({
        id: location.city_id,
        name: location.city_name
      }));
    }
    else if (Array.isArray(data.city_id)) {
      cityArray = data.city_id.map(city => {
        if (typeof city === 'object') {
          return { 
            id: city.id || city.city_id, 
            name: city.name || city.city_name || city.display_name 
          };
        }
        return { id: city, name: city };
      });
    } else if (data.city_id) {
      cityArray = [{ 
        id: data.city_id, 
        name: data.location || data.city_name || data.city_id 
      }];
    } else if (data.location) {
      cityArray = [{ id: data.location, name: data.location }];
    }

    const populatedData = {
      employment_title: data.employment_title || data.title || '',
      slug: data.slug || '',
      employment_type: data.employment_type || data.type || '',
      organisation_id: data.organisation_id || data.organization_id || data.organization || '',
      program_id: data.program_id || data.department || '',
      institute_id: data.institute_id || parseInt(instituteId),
      created_by: data.created_by || parseInt(UserId),
      updated_by: parseInt(UserId),
      short_description: data.short_description || data.shortDescription || '',
      job_description: data.job_description || data.description || data.jobDescription || '',
      openings: data.openings || '1',
      duration: data.duration || '',
      salary: data.salary || '',
      expiry_date: data.expiry_date || '',
      skills: skillsArray,
      education: educationArray,
      industry_id: data.industry_id || data.industry || '',
      degree_status: data.degree_status || data.degreeStatus || '',
      type_of_shift: shiftArray,
      city_id: cityArray,
      status: data.status !== undefined ? data.status.toString() : '1'
    };
    
    console.log('Populated form data:', populatedData);
    setFormData(populatedData);
  };

  const generateSlug = (title, organizationId) => {
    let slug = title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
    
    if (organizationId && organizationData.length > 0) {
      const organization = organizationData.find(org => 
        (org.id && org.id.toString() === organizationId.toString()) || 
        (org.value && org.value.toString() === organizationId.toString())
      );
      
      if (organization) {
        const orgName = organization.organisation_name || organization.name || organization.title || '';
        if (orgName) {
          const orgSlug = orgName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
          slug = `${slug}-${orgSlug}`;
        }
      }
    }
    
    return slug;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'employment_title') {
      let sanitizedValue = value;
      sanitizedValue = value.replace(/[^A-Za-z0-9 ]/g, "");
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue,
        slug: isEditMode ? prev.slug : generateSlug(sanitizedValue, prev.organisation_id)
      }));
    } else if (name === 'organisation_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        slug: isEditMode ? prev.slug : generateSlug(prev.employment_title, value)
      }));
    } else if (name === 'type_of_shift') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? [value] : []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (apiMessage.message) {
      setApiMessage({ type: '', message: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employment_title?.trim()) newErrors.employment_title = 'Employment title is required';
    if (!formData.employment_type) newErrors.employment_type = 'Employment type is required';
    if (!formData.organisation_id) newErrors.organisation_id = 'Organization is required';
    if (!formData.city_id?.length) newErrors.city_id = 'At least one city is required';
    if (!formData.degree_status) newErrors.degree_status = 'Degree status is required';
    if (!formData.openings || formData.openings < 1) newErrors.openings = 'Number of openings is required';
    if (!formData.education?.length) newErrors.education = 'Education requirements are required';
    if (!formData.skills?.length) newErrors.skills = 'Skills are required';
    if (!formData.industry_id) newErrors.industry_id = 'Industry is required';
    if (!formData.type_of_shift?.length) newErrors.type_of_shift = 'Shift type is required';
    if (!formData.short_description?.trim()) newErrors.short_description = 'Short description is required';
    if (!formData.job_description?.trim()) newErrors.job_description = 'Job description is required';
    
    // Job Expiry Date validation
    if (!formData.expiry_date) {
      newErrors.expiry_date = 'Job expiry date is required';
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiryDate = new Date(formData.expiry_date);
      
      if (expiryDate < today) {
        newErrors.expiry_date = 'Job expiry date must be in the future';
      }
    }
    
    if (formData.short_description && formData.short_description.length > 500) {
      newErrors.short_description = 'Short description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatJobPostData = () => {
    const baseData = {
      institute_id: formData.institute_id,
      employment_title: formData.employment_title,
      slug: formData.slug,
      employment_type: formData.employment_type,
      organisation_id: formData.organisation_id,
      program_id: formData.program_id,
      created_by: formData.created_by,
      updated_by: formData.updated_by,
      short_description: formData.short_description,
      job_description: formData.job_description,
      openings: parseInt(formData.openings),
      duration: formData.duration,
      salary: formData.salary,
      expiry_date: formData.expiry_date,
      skills: formData.skills.map(skill => skill.name || skill),
      education: formData.education.map(edu => edu.name || edu.id || edu),
      industry_id: formData.industry_id,
      degree_status: formData.degree_status,
      type_of_shift: formData.type_of_shift,
      city_id: formData.city_id.map(city => city.id || city),
      status: formData.status
    };

    if (isEditMode) {
      baseData.user_id = parseInt(UserId);
    }

    return baseData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiMessage({ type: '', message: '' });
    
    try {
      const formattedData = formatJobPostData();
      let response;
      
      if (isEditMode) {
        if (!jobSlugForUpdate) {
          throw new Error('Job slug not found for update');
        }
        response = await updateEmployment(formattedData, jobSlugForUpdate);
        
        setApiMessage({ 
          type: 'success', 
          message: 'Job post updated successfully!' 
        });
      } else {
        response = await createEmployment(formattedData);
        
        setApiMessage({ 
          type: 'success', 
          message: 'Job post created successfully!' 
        });
      }
      
      if (response && (response.success || response.data || response.id)) {
        if (onSave) {
          await onSave(response.data || response || formattedData);
        }
        
        if (!isEditMode) {
          setFormData({
            employment_title: '',
            slug: '',
            employment_type: '',
            organisation_id: '',
            program_id: '',
            institute_id: parseInt(instituteId),
            created_by: parseInt(UserId),
            updated_by: parseInt(UserId),
            short_description: '',
            job_description: '',
            openings: '',
            duration: '',
            salary: '',
            expiry_date: '',
            skills: [],
            education: [],
            industry_id: '',
            degree_status: '',
            type_of_shift: '',
            city_id: [],
            status: '1'
          });
        }
        
        setTimeout(() => {
          window.location.href = '/job-page'
        }, 2000);
      } else {
        setApiMessage({ type: 'error', message: response?.message || 'Failed to save job post' });
      }
      
    } catch (error) {
      console.error('Error saving job post:', error);
      setApiMessage({ 
        type: 'error', 
        message: error.message || 'An error occurred while saving the job post. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/job-page');
    }
  };

  const handleArrayFieldChange = (fieldName, newValue) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: newValue
    }));
    
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading job data...</span>
        </div>
        <div className="ms-3">
          <h5>Loading job data for editing...</h5>
          <p className="text-muted mb-0">Please wait while we fetch the job details.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Edit Job' : 'Add Job'}
        breadcrumbs={[
          { label: "Dashboard", path: "/institute-dashboard" },
          { label: "Job Listing", path: "/job-page" },
          { label: isEditMode ? 'Edit Job' : 'Add Job', active: true}
        ]}
      />

      {apiMessage.message && (
        <div className={`alert alert-${apiMessage.type === 'success' ? 'success' : 'danger'} text-white alert-dismissible fade show`} role="alert">
          <strong>{apiMessage.type === 'success' ? 'Success!' : 'Error!'}</strong> {apiMessage.message}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setApiMessage({ type: '', message: '' })}
          ></button>
        </div>
      )}

      <div className='mt-4'>
        <div className="row">
          <div className="col-12">
            <div className="card blueborder">
              <div className="card-header blue-card-header">
                <h4 className="card-title">
                  {isEditMode ? 'Edit Employment Information' : 'Create Employment Information'}
                </h4>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <JobInfoSection
                    formData={formData}
                    errors={errors}
                    onInputChange={handleInputChange}
                    onArrayFieldChange={handleArrayFieldChange}
                  />

                  <JobDescriptionSection
                    formData={formData}
                    errors={errors}
                    onInputChange={handleInputChange}
                    onArrayFieldChange={handleArrayFieldChange}
                  />

                  <JobFormAction
                    isSubmitting={isSubmitting}
                    isEdit={isEditMode}
                    onCancel={handleCancel}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstituteAddPostPage;